/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/**
 * Manage a report's email schedules.
 *
 * A schedule runs with the permissions of whoever created it, and that is
 * fixed at creation — so the dialog says so, and only lets you edit your own.
 * Without that stated, "schedule this report to my manager" reads as a
 * delegation when it is actually a standing export of your own view of the
 * data.
 *
 * Cadence is daily / weekly / monthly rather than a cron expression. The job
 * runs hourly, so anything finer could not be honoured, and a cron field is a
 * support burden for the three cadences anyone asks for.
 */

import { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert, Box, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, Divider, IconButton, MenuItem, Switch, TextField, Tooltip,
  Typography,
} from '@mui/material';
import {
  AddRounded, DeleteOutlineRounded, ErrorOutlineRounded, PauseRounded,
  PlayArrowRounded, ScheduleSendRounded,
} from '@mui/icons-material';

import MDButton from 'components/MDButton';
import { useAuth } from 'auth/Auth';
import { useCapability } from 'auth/usePermission';
import colors from 'styles/tokens/colors';

const WEEKDAYS = [
  { value: 0, label: 'Monday' }, { value: 1, label: 'Tuesday' },
  { value: 2, label: 'Wednesday' }, { value: 3, label: 'Thursday' },
  { value: 4, label: 'Friday' }, { value: 5, label: 'Saturday' },
  { value: 6, label: 'Sunday' },
];

const FORMATS = [
  { value: 'csv', label: 'CSV' },
  { value: 'xlsx', label: 'Excel' },
  { value: 'pdf', label: 'PDF' },
];

/** A short list, plus whatever the browser reports, so nobody has to type one. */
function timezones() {
  const common = [
    'UTC', 'Australia/Sydney', 'Australia/Melbourne', 'Australia/Perth',
    'Asia/Kolkata', 'Asia/Dubai', 'Asia/Singapore', 'Europe/London',
    'Europe/Paris', 'America/New_York', 'America/Los_Angeles',
  ];
  const local = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return local && !common.includes(local) ? [local, ...common] : common;
}

const blank = () => ({
  name: '', frequency: 'weekly', day_of_week: 1, day_of_month: 1, hour: 8,
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
  format: 'csv', recipients: [], include_owner: true, skip_if_empty: true,
  is_active: true,
});

export default function ScheduleDialog({ open, onClose, report }) {
  const { axiosInstance, user } = useAuth();
  const canExport = useCapability('EXPORT');

  const [schedules, setSchedules] = useState([]);
  const [draft, setDraft] = useState(null);
  const [recipientText, setRecipientText] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const reportId = report?.id;

  const load = useCallback(async () => {
    if (!reportId) return;
    setLoading(true);
    try {
      const { data } = await axiosInstance.get(
        `/analytics/reports/${reportId}/schedules`);
      setSchedules(Array.isArray(data) ? data : []);
      setError(null);
    } catch (err) {
      setError(readError(err, 'The schedules could not be loaded.'));
    } finally {
      setLoading(false);
    }
  }, [axiosInstance, reportId]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const startNew = () => {
    setDraft(blank());
    setRecipientText('');
  };

  const startEdit = (schedule) => {
    setDraft({ ...schedule });
    setRecipientText((schedule.recipients ?? []).join(', '));
  };

  const save = async () => {
    setSaving(true);
    const recipients = recipientText
      .split(',').map((r) => r.trim()).filter(Boolean);
    const payload = { ...draft, recipients };
    // The server derives these from frequency and rejects a mismatch; sending
    // the irrelevant one would make a daily schedule fail validation on a
    // weekday it does not use.
    if (payload.frequency !== 'weekly') delete payload.day_of_week;
    if (payload.frequency !== 'monthly') delete payload.day_of_month;

    try {
      if (draft.id) {
        await axiosInstance.patch(
          `/analytics/reports/${reportId}/schedules/${draft.id}`, payload);
      } else {
        await axiosInstance.post(
          `/analytics/reports/${reportId}/schedules`, payload);
      }
      setDraft(null);
      setError(null);
      await load();
    } catch (err) {
      setError(readError(err, 'That schedule could not be saved.'));
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (schedule) => {
    try {
      await axiosInstance.patch(
        `/analytics/reports/${reportId}/schedules/${schedule.id}`,
        { is_active: !schedule.is_active });
      await load();
    } catch (err) {
      setError(readError(err, 'That schedule could not be changed.'));
    }
  };

  const remove = async (schedule) => {
    try {
      await axiosInstance.delete(
        `/analytics/reports/${reportId}/schedules/${schedule.id}`);
      await load();
    } catch (err) {
      setError(readError(err, 'That schedule could not be removed.'));
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontWeight: 700 }}>
        Email “{report?.name}”
        <Typography variant="body2" color="text.secondary" mt={0.5}>
          Sent on a schedule, with the data you can see. Recipients get the
          rows your access allows, not theirs.
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {!canExport && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Emailing a report takes data out of the system, so it needs the
            Export permission. Ask an administrator to grant it.
          </Alert>
        )}

        {loading ? (
          <Box display="flex" justifyContent="center" py={3}><CircularProgress size={22} /></Box>
        ) : (
          <>
            {!schedules.length && !draft && (
              <Box sx={{ py: 3, textAlign: 'center' }}>
                <ScheduleSendRounded sx={{ fontSize: 34, opacity: 0.25, mb: 1 }} />
                <Typography variant="body2" color="text.secondary">
                  Not scheduled. Add one to have it emailed automatically.
                </Typography>
              </Box>
            )}

            {schedules.map((schedule) => (
              <ScheduleRow
                key={schedule.id}
                schedule={schedule}
                mine={schedule.run_as_user_id === user?.id}
                onEdit={() => startEdit(schedule)}
                onToggle={() => toggle(schedule)}
                onRemove={() => remove(schedule)}
              />
            ))}

            {draft && (
              <>
                {schedules.length > 0 && <Divider sx={{ my: 2 }} />}
                <Box display="flex" flexDirection="column" gap={1.5}>
                  <TextField
                    size="small" fullWidth label="Subject (optional)"
                    value={draft.name ?? ''}
                    placeholder={`${report?.name} — scheduled report`}
                    onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
                  />

                  <Box display="flex" gap={1}>
                    <TextField
                      select size="small" label="How often" sx={{ width: 130 }}
                      value={draft.frequency}
                      onChange={(e) => setDraft((d) => ({ ...d, frequency: e.target.value }))}
                    >
                      <MenuItem value="daily">Daily</MenuItem>
                      <MenuItem value="weekly">Weekly</MenuItem>
                      <MenuItem value="monthly">Monthly</MenuItem>
                    </TextField>

                    {draft.frequency === 'weekly' && (
                      <TextField
                        select size="small" label="Day" sx={{ width: 140 }}
                        value={draft.day_of_week ?? 1}
                        onChange={(e) => setDraft((d) => ({
                          ...d, day_of_week: Number(e.target.value) }))}
                      >
                        {WEEKDAYS.map((d) => (
                          <MenuItem key={d.value} value={d.value}>{d.label}</MenuItem>
                        ))}
                      </TextField>
                    )}

                    {draft.frequency === 'monthly' && (
                      <TextField
                        select size="small" label="Day of month" sx={{ width: 140 }}
                        value={draft.day_of_month ?? 1}
                        onChange={(e) => setDraft((d) => ({
                          ...d, day_of_month: Number(e.target.value) }))}
                        // 28 is the ceiling on purpose: a schedule set for the
                        // 31st would skip February and the short months.
                        helperText="1–28"
                      >
                        {Array.from({ length: 28 }, (_, i) => i + 1).map((day) => (
                          <MenuItem key={day} value={day}>{day}</MenuItem>
                        ))}
                      </TextField>
                    )}

                    <TextField
                      select size="small" label="At" sx={{ width: 100 }}
                      value={draft.hour}
                      onChange={(e) => setDraft((d) => ({ ...d, hour: Number(e.target.value) }))}
                    >
                      {Array.from({ length: 24 }, (_, h) => h).map((h) => (
                        <MenuItem key={h} value={h}>
                          {String(h).padStart(2, '0')}:00
                        </MenuItem>
                      ))}
                    </TextField>
                  </Box>

                  <Box display="flex" gap={1}>
                    <TextField
                      select size="small" fullWidth label="Timezone"
                      value={draft.timezone}
                      onChange={(e) => setDraft((d) => ({ ...d, timezone: e.target.value }))}
                    >
                      {timezones().map((zone) => (
                        <MenuItem key={zone} value={zone}>{zone}</MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      select size="small" label="Format" sx={{ width: 120 }}
                      value={draft.format}
                      onChange={(e) => setDraft((d) => ({ ...d, format: e.target.value }))}
                    >
                      {FORMATS.map((f) => (
                        <MenuItem key={f.value} value={f.value}>{f.label}</MenuItem>
                      ))}
                    </TextField>
                  </Box>

                  <TextField
                    size="small" fullWidth label="Also send to"
                    value={recipientText}
                    onChange={(e) => setRecipientText(e.target.value)}
                    placeholder="someone@example.com, someone.else@example.com"
                    helperText="Separate addresses with commas."
                  />

                  <Box display="flex" alignItems="center" gap={1}>
                    <Switch
                      size="small" checked={draft.include_owner}
                      onChange={(e) => setDraft((d) => ({
                        ...d, include_owner: e.target.checked }))}
                    />
                    <Typography variant="body2">Send to me as well</Typography>
                  </Box>

                  <Box display="flex" alignItems="flex-start" gap={1}>
                    <Switch
                      size="small" checked={draft.skip_if_empty}
                      onChange={(e) => setDraft((d) => ({
                        ...d, skip_if_empty: e.target.checked }))}
                    />
                    <Box>
                      <Typography variant="body2">
                        Skip the email when there is nothing to report
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        An empty &ldquo;Stalled Deals&rdquo; is good news; mailing it
                        anyway teaches people to ignore the series.
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        {draft ? (
          <>
            <MDButton variant="text" color="dark" onClick={() => setDraft(null)}>
              Cancel
            </MDButton>
            <MDButton variant="gradient" color="info" onClick={save}
                      disabled={saving || !canExport}>
              {saving ? 'Saving…' : draft.id ? 'Save schedule' : 'Create schedule'}
            </MDButton>
          </>
        ) : (
          <>
            <MDButton variant="text" color="dark" onClick={onClose}>Done</MDButton>
            <MDButton variant="gradient" color="info" onClick={startNew}
                      disabled={!canExport}>
              <AddRounded sx={{ mr: 0.5, fontSize: 18 }} /> Add a schedule
            </MDButton>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}

ScheduleDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  report: PropTypes.object,
};

function ScheduleRow({ schedule, mine, onEdit, onToggle, onRemove }) {
  const cadence = schedule.frequency === 'daily'
    ? 'Every day'
    : schedule.frequency === 'weekly'
      ? `Every ${WEEKDAYS.find((d) => d.value === schedule.day_of_week)?.label ?? '—'}`
      : `Day ${schedule.day_of_month} each month`;

  const stalled = schedule.failure_count > 0;

  return (
    <Box
      sx={{
        display: 'flex', alignItems: 'center', gap: 1.5, px: 1.5, py: 1.25,
        borderRadius: '10px', mb: 0.5,
        backgroundColor: colors.background.offWhite,
        opacity: schedule.is_active ? 1 : 0.55,
      }}
    >
      <ScheduleSendRounded sx={{ fontSize: 18, color: colors.text.light }} />
      <Box flex={1} minWidth={0}>
        <Typography variant="body2" fontWeight={600} noWrap>
          {cadence} at {String(schedule.hour).padStart(2, '0')}:00{' '}
          <Typography component="span" variant="caption" color="text.secondary">
            {schedule.timezone}
          </Typography>
        </Typography>
        <Typography variant="caption" color="text.secondary" noWrap display="block">
          {schedule.format.toUpperCase()}
          {schedule.recipients?.length
            ? ` · ${schedule.recipients.length} recipient(s)` : ''}
          {schedule.last_sent_at
            ? ` · last sent ${new Date(schedule.last_sent_at).toLocaleDateString()}`
            : ' · not sent yet'}
        </Typography>
      </Box>

      {stalled && (
        <Tooltip title={schedule.last_error || 'This schedule is failing'}>
          <ErrorOutlineRounded sx={{ fontSize: 17, color: colors.status.error.dot }} />
        </Tooltip>
      )}

      {!schedule.is_active && (
        <Chip size="small" label="Paused" sx={{ height: 19, fontSize: 10 }} />
      )}

      {mine ? (
        <>
          <Tooltip title={schedule.is_active ? 'Pause' : 'Resume'}>
            <IconButton size="small" onClick={onToggle}>
              {schedule.is_active
                ? <PauseRounded sx={{ fontSize: 16 }} />
                : <PlayArrowRounded sx={{ fontSize: 16 }} />}
            </IconButton>
          </Tooltip>
          <MDButton variant="text" color="info" size="small" onClick={onEdit}>
            Edit
          </MDButton>
          <Tooltip title="Remove">
            <IconButton size="small" onClick={onRemove}>
              <DeleteOutlineRounded sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </>
      ) : (
        // Someone else's schedule runs with their access to the data, so
        // repointing its recipients would mail their rows anywhere.
        <Tooltip title="Only the person who created this can change it — it runs with their access">
          <Typography variant="caption" color="text.secondary">
            Someone else&apos;s
          </Typography>
        </Tooltip>
      )}
    </Box>
  );
}

function readError(err, fallback) {
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail) && detail[0]?.msg) return detail[0].msg;
  if (detail?.message) return detail.message;
  return fallback;
}
